/**
 * Burton Forms — core: namespace, id source, ready signal, rule registry.
 * Layer 1 (no DOM). Loads after BAO core.
 */
window.BAO = window.BAO || {}
window.BAO.Forms = window.BAO.Forms || {}

/**
 * Monotonic id source — unique per-instance ids so multiple forms/blocks on one page never collide.
 * @returns {number}
 */
let idCounter = 0
window.BAO.Forms.nextId =
  window.BAO.Forms.nextId ||
  function nextId () {
    idCounter += 1

    return idCounter
  }

// Ready signal for imperative consumers (e.g. an app registering a rule). The loader resolves it
// once all modules are defined. Element-based use doesn't need it — upgrade is order-safe.
if (!window.BAO.Forms.ready) {
  let resolveReady

  window.BAO.Forms.ready = new Promise((resolve) => {
    resolveReady = resolve
  })

  window.BAO.Forms._markReady = function _markReady () {
    if (resolveReady) resolveReady()
  }
}

/**
 * @typedef {true|string} ValidatorResult - `true` when valid, else the error message.
 * @typedef {(value: string, ctx: Object) => ValidatorResult|Promise<ValidatorResult>} Validator
 * @typedef {Object} RuleDefinition
 * @property {Validator} test
 * @property {string|((ctx: Object) => string)|Object.<string,string>} message  string, fn, or locale-keyed object ({ en, fr, 'fr-CA', … }), resolved against the form's locale most-specific-first (exact locale → language → `en`), so regional keys are a sparse override rather than a full duplicate set
 */

/**
 * Two-tier rule registry: built-ins register { internal: true } and can't be clobbered by app
 * calls. Unknown lookups return undefined, which the field treats as a fail-closed dev error.
 *
 * @returns {{ register: Function, has: Function, get: Function }}
 */
function createRuleRegistry () {
  const rules = new Map()
  const builtInRuleNames = new Set()

  return {
    /**
     * @param {string} name
     * @param {RuleDefinition} definition
     * @param {{ internal?: boolean }} [options]
     */
    register (name, definition, options = {}) {
      if (builtInRuleNames.has(name) && !options.internal) {
        // eslint-disable-next-line no-console
        console.warn(
          `[BAO.Forms] "${name}" is a built-in rule and cannot be overridden.`,
        )

        return
      }

      if (options.internal) builtInRuleNames.add(name)

      rules.set(name, definition)
    },

    /**
     * @param {string} name
     * @returns {boolean}
     */
    has (name) {
      return rules.has(name)
    },

    /**
     * @param {string} name
     * @returns {RuleDefinition|undefined}
     */
    get (name) {
      return rules.get(name)
    },

    /**
     * Override just the `message` of an existing rule (built-in or app), leaving its `test` intact —
     * so an app can localize/re-word a built-in without clobbering its logic. `message` may be a
     * string, an `(ctx) => string` function, or a locale-keyed object `{ en, fr, 'fr-CA', … }`.
     *
     * @param {string} name
     * @param {string|Function|Object.<string,string>} message
     */
    setMessage (name, message) {
      const definition = rules.get(name)

      if (definition) {
        rules.set(name, Object.assign({}, definition, { message }))
      }
    },
  }
}

window.BAO.Forms.rules = window.BAO.Forms.rules || createRuleRegistry()
